import type { Article } from "../articles";
import { PRICING_2026 } from "../guides-data";

const r = PRICING_2026.roofing;

export const articleDoesHomeownersInsuranceCoverRoofReplacement: Article = {
  slug: "does-homeowners-insurance-cover-roof-replacement",
  title: "Does Homeowners Insurance Cover Roof Replacement? What's Covered, What's Excluded, and How Claims Work",
  description: "Standard homeowners insurance covers roof damage from sudden events, hail, wind, fallen trees, but not wear and tear, age, or lack of maintenance. The payout amount depends on whether your policy covers Replacement Cost Value or Actual Cash Value.",
  category: "Insurance",
  tags: ["Insurance", "Roofing", "Claims", "Home Coverage", "Storm Damage"],
  createdAt: "June 21, 2026",
  updatedAt: "July 1, 2026",
  lastVerified: "July 1, 2026",
  readTime: "9 min read",
  keyTakeaway: `Insurance covers roof damage from sudden, accidental events (wind, hail, fallen trees) but NOT from age, wear and tear, or lack of maintenance. Claims are denied if the roof is over 20 years old or damage is deemed cosmetic.`,
  author: { name: "Mauricio L.", url: "/authors/mauricio-l/" },
  sources: [
    {
      name: "Insurance Information Institute (III) - Roofing Claims",
      url: "https://www.iii.org"
    },
    {
      name: "National Association of Insurance Commissioners (NAIC)",
      url: "https://www.naic.org"
    }
  ],
  keyMetrics: [
    { label: "Avg Roof Replacement", value: `$${r.averageTotalCost.min.toLocaleString()}-$${r.averageTotalCost.max.toLocaleString()}` },
    { label: "Typical Deductible", value: "$500-$2,500" },
    { label: "Roof Age Limit", value: "15-20 years for full coverage" },
    { label: "Claim Decision Time", value: "15-30 days" }
  ],
  faqs: [
    {
      question: "Will my insurance cover a 15-year-old roof?",
      answer: "Maybe. If the roof is 15+ years old, insurers often prorate coverage based on remaining lifespan (Actual Cash Value vs Replacement Cost Value). A 20-year-old roof with a 20-year lifespan has zero remaining value: you get nothing. Roofs under 15 years typically get full replacement cost coverage."
    },
    {
      question: "What if only part of my roof is damaged?",
      answer: "Insurers may pay for partial replacement (repair only damaged sections). However, if matching is an issue (old shingles aren't available), some policies cover full replacement. Florida and some coastal states have 'matching clauses' that require full replacement if partial replacement can't match."
    },
    {
      question: "How do I prove storm damage vs wear and tear?",
      answer: "Hail damage shows as random bruising or granule loss. Wind damage appears as lifted or missing shingles along edges. Wear and tear shows as uniform aging, curling, or missing granules across the entire roof. Adjusters look for patterns, storm damage is localized; wear is widespread."
    }
  ],
  content: `
    <p>
      Homeowners insurance is designed to cover sudden, accidental damage, not gradual deterioration. For roofs, this means a wind storm that tears off shingles is covered; a roof that has aged past its service life and developed leaks over time is not. The covered cause has to be a specific, identifiable event, not normal wear.
    </p>
    <p>
      The second variable that determines the size of a payout is your policy's valuation method: Replacement Cost Value (RCV), which pays the full cost to replace the roof minus your deductible, versus Actual Cash Value (ACV), which applies depreciation based on the roof's age and reduces the payout accordingly. These two methods can produce payouts that differ by thousands of dollars on the same claim.
    </p>

    <div class="my-6 p-4 rounded-lg bg-surface border border-border-custom">
      <p class="font-bold text-foreground m-0 mb-2">The Short Version</p>
      <p class="text-sm text-text-muted m-0">
        Covered: wind, hail, fallen trees, fire, ice/snow weight, sudden, accidental damage. Not covered: age, wear and tear, deferred maintenance, manufacturing defects, floods, earthquakes. Roofs over 15–20 years old often receive Actual Cash Value (depreciated) payouts, not full replacement cost. Check your policy for "ACV endorsement" or "roof age limitations" language before filing. The typical deductible is $500–$2,500, or a percentage of the home's insured value in coastal states.
      </p>
      <p class="text-xs text-text-muted mt-2 m-0">
        <em>Coverage principles per Insurance Information Institute (III) and NAIC policyholder guidance.</em>
      </p>
    </div>

    <h2>What this article covers:</h2>
    <ul>
      <li>What standard HO-3 policies cover (and what they exclude)</li>
      <li>Replacement Cost Value vs. Actual Cash Value: how the difference is calculated</li>
      <li>Step-by-step claim filing process</li>
      <li>Common denial reasons and how to respond</li>
    </ul>

    <h2>What Homeowners Insurance DOES Cover</h2>
    <p>
      Standard HO-3 policies (the most common type) cover roof damage caused by "perils" listed in your policy:
    </p>
    <ul>
      <li><strong>Wind storms:</strong> Shingles torn off by high winds. Gusts must typically exceed policy thresholds (often 60+ mph).</li>
      <li><strong>Hail:</strong> Bruised or cracked shingles from hail impact. Hail size matters, golf ball-sized or larger almost always qualifies.</li>
      <li><strong>Fallen objects:</strong> Trees, limbs, or debris that physically damage the roof.</li>
      <li><strong>Fire or lightning:</strong> Fire damage or lightning strikes that puncture or burn the roof.</li>
      <li><strong>Weight of ice or snow:</strong> Collapse or structural damage from accumulated ice/snow (common in northern states).</li>
      <li><strong>Vandalism:</strong> Intentional damage during break-ins or malicious acts.</li>
    </ul>
    <p>
      Key phrase: <strong>"sudden and accidental."</strong> Insurance is designed for unexpected events, not gradual deterioration.
    </p>

    <h2>What Homeowners Insurance Does NOT Cover</h2>
    <p>
      These scenarios result in denied claims:
    </p>
    <ul>
      <li><strong>Age and wear and tear:</strong> A 25-year-old asphalt roof that has <a href="/articles/roof-lifespan-by-material/" class="text-[var(--accent)] hover:underline font-semibold">reached the end of its lifespan</a> isn't covered. Shingles don't last forever.</li>
      <li><strong>Lack of maintenance:</strong> If you ignored small leaks that became big problems, or skipped repairs that led to rot, the damage isn't covered.</li>
      <li><strong>Manufacturing defects:</strong> If shingles failed prematurely due to defects, that's a warranty claim against the manufacturer, not an insurance claim.</li>
      <li><strong>Earthquakes and floods:</strong> Require separate policies. Standard homeowners insurance excludes these.</li>
      <li><strong>Cosmetic damage:</strong> Hail that dents but doesn't crack shingles may be deemed "cosmetic" and not covered. Some states ban this exclusion.</li>
    </ul>
    <p>
      The most common denial reason: <strong>"failure to maintain."</strong> If the adjuster determines your roof was already deteriorating before the storm, the claim gets denied or reduced.
    </p>

    <!--COMPONENT:pullquote-->Never assume you have a flat deductible for storm damage. In coastal states, windstorm deductibles are often written as 1% to 5% of your home's total insured value.<!--ENDOFCOMPONENT-->

    <!--COMPONENT:warning-note|label="Reality Check"-->
    Insurance covers sudden damage, not gradual deterioration. If your roof is 20+ years old, expect Actual Cash Value (depreciated) payouts, not full replacement cost. Many policies have "roof age limits" that reduce or eliminate coverage for roofs over 15-20 years old. Check your policy before filing.
    <!--ENDOFCOMPONENT-->

    <h2>Replacement Cost vs Actual Cash Value</h2>
    <p>
      This distinction determines how much you get paid:
    </p>
    <ul>
      <li><strong>Replacement Cost Value (RCV):</strong> Pays the full cost to replace your roof with similar materials, minus your deductible. No depreciation applied.</li>
      <li><strong>Actual Cash Value (ACV):</strong> Pays RCV minus depreciation. A 15-year-old roof with a 25-year lifespan has 40% remaining value. You get 40% of replacement cost, minus deductible.</li>
    </ul>
    <p>
      Example: Your roof replacement costs $15,000. It's 15 years old with a 25-year lifespan (40% remaining value).
    </p>
    <ul>
      <li><strong>RCV payout:</strong> $15,000 - $1,000 deductible = $14,000</li>
      <li><strong>ACV payout:</strong> ($15,000 × 40%) - $1,000 deductible = $5,000</li>
    </ul>
    <p>
      That's a $9,000 difference. Most policies cover RCV for roofs under 10-15 years old, then switch to ACV for older roofs. Some policies have "functional replacement cost" clauses that pay for the cheapest adequate repair, not full replacement.
    </p>

    <h2>How to File a Roof Claim (Step by Step)</h2>
    <ol>
      <li><strong>Document the damage:</strong> Refer to our guide on <a href="/articles/how-to-photograph-roof-for-insurance-claim/" class="text-[var(--accent)] hover:underline font-semibold">how to photograph your roof for an insurance claim</a>. Take photos from the ground and (safely) from the roof. Photograph granules in gutters, missing shingles, and any interior water damage.</li>
      <li><strong>Make temporary repairs:</strong> Tarp exposed areas to prevent further damage. Keep receipts, insurance reimburses reasonable temporary repairs.</li>
      <li><strong>File the claim:</strong> Call your insurer within 24-48 hours. Provide date of loss, cause (if known), and initial damage assessment.</li>
      <li><strong>Get an independent estimate:</strong> Hire a reputable roofing contractor to provide a written estimate. Don't sign a contract yet, wait for the adjuster.</li>
      <li><strong>Meet the adjuster:</strong> Walk the roof with the adjuster if possible. Point out all damage. Ask questions about their assessment.</li>
      <li><strong>Review the settlement:</strong> The adjuster sends a report with their findings and payout amount. Compare it to your contractor's estimate.</li>
      <li><strong>Negotiate if needed:</strong> If the payout seems low, provide your contractor's estimate and request a re-inspection. Supplemental claims are common.</li>
    </ol>

    <h2>When Claims Get Denied (And What to Do)</h2>
    <p>
      Common denial reasons and how to fight back:
    </p>
    <ul>
      <li><strong>"Wear and tear, not storm damage":</strong> Get an independent engineer or roofing contractor to write a report attributing damage to the storm. Submit with a demand letter.</li>
      <li><strong>"Roof is over 20 years old":</strong> If you have RCV coverage, argue that age doesn't matter, only the cause of damage matters. Cite your policy language.</li>
      <li><strong>"Damage is cosmetic":</strong> In states with anti-cosmetic damage laws (Texas, Colorado, others), this exclusion is illegal. File a complaint with your state insurance commissioner.</li>
      <li><strong>"Insufficient evidence":</strong> Provide dated photos (before/after the storm), contractor reports, and weather service data confirming hail/wind in your area.</li>
    </ul>

    <!--COMPONENT:warning-note|label="Storm Damage Filing Deadline"-->
    Most homeowners insurance policies impose a strict filing deadline, typically 12 to 24 months from the date of the storm event. Waiting too long to file, even if you are unaware of the damage, is one of the most common reasons claims are denied. Inspect your roof immediately after any severe storm, gather meteorological reports, and file your claim promptly to protect your coverage.
    <!--ENDOFCOMPONENT-->

    <p>
      If your claim is denied and you believe it's wrongful, file an appeal with your insurer. If that fails, contact your state's insurance department or hire a public adjuster (they take 10-20% of the settlement but maximize payouts).
    </p>

    <h2>Will My Rates Go Up After a Roof Claim?</h2>
    <p>
      Probably. A single roof claim typically increases premiums by 10-20% for 3-5 years. Multiple claims within 5 years can lead to non-renewal. Some insurers offer "claims-free forgiveness" for the first claim, ask if your policy has this.
    </p>
    <p>
      If the damage is minor (repairs cost $3,000-$5,000), consider paying out-of-pocket rather than filing a claim. The rate increase over 5 years might exceed the claim payout.
    </p>

    <p>
      Before filing any roof claim, calculate whether it's cost-effective. A claim that pays $3,000 after deductible but raises your annual premium by $600 for the next 5 years costs you the same amount you recovered, without the rate increase benefit. For damage that falls close to your deductible amount, paying out-of-pocket preserves your claims history. For large, clearly storm-attributable damage on a young roof with RCV coverage, filing is straightforward.
    </p>
    <p>
      Review your declarations page before any claim to confirm your valuation method (RCV vs. ACV), your deductible amount, and whether your policy includes a roof age limitation endorsement. These three items determine what you'll actually receive.
    </p>
  `
};